/**
 * Auto-apply: the control panel for automatic applying (E14).
 *
 * Split out of Preferences so it has a home of its own in the nav — the one
 * screen that turns Auto on, sets the rules, connects this browser and shows
 * the daily limit. It loads and saves the whole preferences row, so it never
 * disturbs the search fields set on the Search screen.
 *
 * The three ways an application goes out — Autopilot (cloud), Copilot (your
 * own browser) and Manual (you press submit) — are explained in the Guide.
 */

import { useCallback, useEffect, useState } from "react";
import { hasState } from "./usStates";
import { api, ApiError } from "./auth";
import { RadioGroup, ErrorList } from "./fields";
import { findExtension, handRunnerKey, type ExtensionStatus } from "./extension";
import type { Preferences } from "./Preferences";

interface RunnerKeyView {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

function browserLabel(): string {
  const ua = navigator.userAgent;
  const os = /Mac/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /CrOS/.test(ua) ? "Chromebook" : /Linux/.test(ua) ? "Linux" : "this computer";
  const browser = /Edg\//.test(ua) ? "Edge" : /Brave/.test(ua) ? "Brave" : "Chrome";
  return `${browser} on ${os}`;
}

function whenWords(iso: string | null): string {
  if (!iso) return "never";
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

const APPLY_MODES = [
  {
    value: "review" as const,
    label: "Show me matches first",
    note: "We queue them; nothing goes out until you approve it.",
  },
  {
    value: "auto" as const,
    label: "Apply automatically",
    note: "JobCork finds them, fills them and sends them — in the cloud with your computer off (Autopilot), or in your browser when it is open (Copilot) — by the rules below, within your daily limit, with a full record of every field. Anything it cannot answer from your confirmed record is left for you instead of guessed.",
  },
];

/** What the readiness list reads: the answers and the resume in use. */
interface ReadinessAnswers {
  confirmedAt: string | null;
  homeCity: string | null;
  homePostalCode: string | null;
  smsOptIn?: boolean | null;
  openToOffice?: boolean | null;
  homeCountry: string | null;
  currentCompany: string | null;
  currentTitle: string | null;
  acceptStandardConsents: boolean;
  letModelAnswer: boolean;
  linkedinUrl?: string | null;
}

/**
 * Before Auto sends (E14): the handful of answers forms ask for again and
 * again, checked against what the person has given, so a form is not left
 * for them over a postal code. Each missing one links to where it is set.
 */
function Readiness({ connected, stamp, onGo }: { connected: boolean; stamp: number | null; onGo: (go: string) => void }) {
  const [answers, setAnswers] = useState<ReadinessAnswers | null>(null);
  const [resume, setResume] = useState<boolean | null>(null);
  useEffect(() => {
    api<{ answers: ReadinessAnswers }>("/api/answers").then((r) => setAnswers(r.answers)).catch(() => setAnswers(null));
    api<{ resume: unknown }>("/api/resume").then((r) => setResume(!!r.resume)).catch(() => setResume(null));
  }, [stamp]);
  if (!answers || resume === null) return null;
  const has = (v: string | null | undefined) => !!v && v.trim().length > 0;
  const items: Array<{ ok: boolean; text: string; where: string | null }> = [
    { ok: !!answers.confirmedAt, text: "Application answers confirmed", where: "answers" },
    { ok: resume, text: "A resume in use", where: "resume" },
    { ok: hasState(answers.homeCity), text: "State beside your city (“Nashville, TN”) — state boxes, location pickers and your time zone read it", where: "answers" },
    { ok: has(answers.homePostalCode), text: "Postal code — many forms ask it", where: "answers" },
    { ok: answers.smsOptIn === true || answers.smsOptIn === false, text: "Text-message opt-in — one of the most-asked questions", where: "answers" },
    { ok: answers.openToOffice === true || answers.openToOffice === false, text: "Open to some in-office time — “in-person 25% of the time?” questions", where: "answers" },
    { ok: has(answers.homeCountry), text: "Country — for “are you located in…” questions", where: "answers" },
    { ok: has(answers.currentCompany) && has(answers.currentTitle), text: "Current company and title", where: "answers" },
    { ok: answers.acceptStandardConsents, text: "Standard privacy boxes may be ticked — without it, most forms are left for you", where: "answers" },
    { ok: answers.letModelAnswer, text: "Employers' own written questions may be answered from your resume — without it, a form with an essay box is left for you", where: "answers" },
    { ok: connected, text: "A connected browser, or the cloud switched on", where: null },
  ];
  const ready = items.filter((i) => i.ok).length;
  return (
    <div className="readiness">
      <p className="small"><strong>Before Auto sends</strong> <span className="muted">— {ready} of {items.length} ready</span></p>
      <ul className="readiness-list small">
        {items.map((i) => (
          <li key={i.text} className={i.ok ? "ok" : "missing"}>
            <span className="readiness-mark" aria-hidden="true">{i.ok ? "✓" : "○"}</span>
            <span>
              {i.text}
              {!i.ok && i.where ? <> · <button className="step-link small" onClick={() => onGo(i.where!)}>set it</button></> : null}
            </span>
          </li>
        ))}
      </ul>
      {ready < items.length && <p className="muted small">A form that asks for one of the missing ones is left for you as “needs you” instead of being sent.</p>}
    </div>
  );
}

function AutoRules({ prefs, set, ext, keys, onConnect, connecting, note, stamp, onGo }: {
  prefs: Preferences;
  set: <K extends keyof Preferences>(k: K, v: Preferences[K]) => void;
  ext: ExtensionStatus | null;
  keys: RunnerKeyView[];
  onConnect: () => void;
  connecting: boolean;
  note: string | null;
  stamp: number | null;
  onGo: (go: string) => void;
}) {
  const here = ext?.runner?.connected ?? false;
  return (
    <div className="notice auto-rules">
      <Readiness connected={keys.length > 0 || prefs.autoCloud} stamp={stamp} onGo={onGo} />
      <p className="small"><strong>Auto's rules</strong></p>
      <div className="pair">
        <div className="field">
          <label className="field-label" htmlFor="auto-min">Only matches scoring at least</label>
          <input id="auto-min" className="input narrow" type="number" min={0} max={100} value={prefs.autoMinScore}
                 onChange={(e) => set("autoMinScore", Number(e.target.value))} />
          <p className="field-hint muted small">The number on the match card. 60 is a strong match; 80 is rare.</p>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="auto-age">Only postings newer than</label>
          <input id="auto-age" className="input narrow" type="number" min={1} max={90} value={prefs.autoMaxAgeDays}
                 onChange={(e) => set("autoMaxAgeDays", Number(e.target.value))} />
          <p className="field-hint muted small">Days. Older postings are mostly filled.</p>
        </div>
      </div>
      <label className="choice">
        <input type="checkbox" checked={prefs.autoStatedPayOnly} onChange={(e) => set("autoStatedPayOnly", e.target.checked)} />
        <span>Skip postings that do not state pay</span>
      </label>
      <p className="muted small">
        Always: your titles, places and pay floor obeyed exactly; never an employer you hid; never twice to the same posting;
        never a form with a required question your confirmed answers cannot cover — that one goes to Applications marked “needs you”.
      </p>

      <p className="small"><strong>Where it runs</strong></p>
      <label className="choice">
        <input type="checkbox" checked={prefs.autoCloud} onChange={(e) => set("autoCloud", e.target.checked)} />
        <span>Also run in the cloud when your browser is closed <span className="muted small">(Autopilot — a browser JobCork hosts works the same queue by the same rules; some sites ask it for a human check, and those postings come to you as “needs you” — with an email the moment it happens, so the one click left is yours within minutes, not when you next look)</span></span>
      </label>
      {ext && !ext.installed && (
        <p className="small err">The JobCork extension is not installed in this browser. Copilot runs inside the extension — see the Guide to set it up, then save again.</p>
      )}
      {ext?.installed && here && (
        <>
          <p className="small ok">Connected in this browser{ext.runner?.label ? ` (${ext.runner.label})` : ""}. {ext.runner?.line}</p>
          <p className="muted small">
            This browser (Copilot) works the queue whenever your computer is on: the moment you open Chrome or come back to your computer, and on after the last window closes
            (on Windows, from sign-in). It goes first to what only your browser can send — the postings where the cloud was asked for a robot check — and opens a
            minimized window for each one when none is open. Quitting Chrome stops it.
          </p>
        </>
      )}
      {ext?.installed && !here && (
        <p className="small">
          Not yet connected in this browser.{" "}
          <button className="step-link small" disabled={connecting} onClick={onConnect}>{connecting ? "Connecting…" : "Connect this browser"}</button>
        </p>
      )}
      {keys.length > 0 && (
        <p className="muted small">
          Browsers connected: {keys.map((k) => `${k.label} (since ${new Date(k.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}, last worked ${whenWords(k.lastUsedAt)})`).join("; ")}.
          Switching to “Show me matches first” disconnects them all.
        </p>
      )}
      {note && <p className="small muted">{note}</p>}
      <QueuePreview stamp={stamp} maxAge={prefs.autoMaxAgeDays} />
    </div>
  );
}

interface QueueAnswer {
  mode: string;
  items: Array<{ jobId: string; title: string; employer: string; ats: string; score: number }>;
  skipped: { score: number; age: number; pay: number; ats: number; applied: number; attempted: number; checked?: number; flagged?: number } | null;
  refused?: Array<{ jobId: string; code: string }>;
  note?: string | null;
  sentToday?: number;
  budget?: { dailyRemaining: number };
  /** How many ranked matches the rules were applied to. */
  considered?: number;
}

const REFUSAL_WORDS: Record<string, string> = {
  filter_title_mismatch: "the title is not one you asked for",
  filter_location_mismatch: "not in a place you selected",
  filter_abroad: "remote, but based in another country",
  filter_comp_below_floor: "posted pay below your floor",
  filter_employer_excluded: "an employer you hid",
  answer_profile_incomplete: "your answers are not confirmed",
  daily_budget_exhausted: "today's limit is used",
  monthly_budget_exhausted: "this month's limit is used",
  rate_limited: "pacing to that site",
  already_applied: "already applied",
  job_closed: "the posting closed",
};

/**
 * What Auto would send today, by the rules as saved: the queue's own
 * accounting, so a person can see why nothing is going out (54 below the
 * score, 13 too old) and turn the right knob. Read as a rehearsal, so it
 * works in Review too.
 */
function QueuePreview({ stamp, maxAge }: { stamp: number | null; maxAge: number }) {
  const [q, setQ] = useState<QueueAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setQ(null);
    api<QueueAnswer>("/api/apply/queue?limit=5&rehearsal=1").then(setQ).catch((e) => setError(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message));
  }, [stamp]);
  if (error) return <p className="err small">{error}</p>;
  if (!q) return <p className="muted small">Working out what Auto would send today…</p>;
  const sk = q.skipped;
  const refused = q.refused ?? [];
  const byCode = new Map<string, number>();
  for (const r of refused) byCode.set(r.code, (byCode.get(r.code) ?? 0) + 1);
  const reasons = [
    sk && sk.score > 0 ? `${sk.score} below your score` : null,
    sk && sk.age > 0 ? `${sk.age} older than ${maxAge} days` : null,
    sk && sk.pay > 0 ? `${sk.pay} without stated pay` : null,
    sk && sk.ats > 0 ? `${sk.ats} on a system JobCork cannot fill (Workday)` : null,
    sk && sk.applied > 0 ? `${sk.applied} already applied to` : null,
    sk && sk.attempted > 0 ? `${sk.attempted} Auto already tried` : null,
    sk && (sk.checked ?? 0) > 0 ? `${sk.checked} at employers where the cloud gets a human check — those go to your browser (Copilot)` : null,
    sk && (sk.flagged ?? 0) > 0 ? `${sk.flagged} at employers whose form refuses sends JobCork presses itself — those come to you to press Submit (one click each, from Applications)` : null,
    ...[...byCode.entries()].map(([code, n]) => `${n} refused by the gate (${REFUSAL_WORDS[code] ?? code.replace(/_/g, " ")})`),
  ].filter(Boolean);
  return (
    <div className="queue-preview">
      <p className="small"><strong>What Auto would send today</strong> <span className="muted">— by the rules as saved</span></p>
      {q.note && q.items.length === 0 && <p className="muted small">{q.note}</p>}
      {q.items.length > 0 ? (
        <ul className="small queue-list">
          {q.items.map((it) => <li key={it.jobId}>{it.title} <span className="muted">— {it.employer} · {it.score}</span></li>)}
        </ul>
      ) : reasons.length > 0 ? (
        <p className="small">Nothing right now.</p>
      ) : null}
      {reasons.length > 0 && (
        <p className="muted small">
          Of the {typeof q.considered === "number" ? `${q.considered} best-ranked matches` : "matches"} Auto reads: {reasons.join("; ")}.
          {typeof q.considered === "number" && <> The dashboard's larger number counts every fillable posting that meets your filters, whatever its score or age; Auto sends only what clears the rules above.</>}
        </p>
      )}
      {typeof q.sentToday === "number" && q.sentToday > 0 && <p className="muted small">{q.sentToday} sent today; {q.budget?.dailyRemaining ?? 0} left under your limit.</p>}
    </div>
  );
}

interface StopsAnswer {
  window: { days: number; from: string; to: string };
  fillsLeft: number;
  applications: number;
  /** Applications still waiting on the person over a question, all time: what "Try them all again" puts back in the queue. */
  requeueable?: number;
  byBucket: Array<{ bucket: string; words: string; count: number }>;
  questions: Array<{ key: string; label: string; bucket: string; why: string; count: number; employers: string[]; lastAt: string }>;
  sends: Array<{ stop: string; count: number; employers: string[]; sample: string; lastAt: string }>;
  fails?: Array<{ note: string; count: number; employers: string[]; lastAt: string }>;
}

/** Where each bucket's fix lives, as a link the person can follow. */
const BUCKET_GO: Record<string, { go: string; words: string } | undefined> = {
  answer: { go: "answers", words: "Answers" },
  consent: { go: "answers", words: "Answers" },
  unknown_question: { go: "applications", words: "Applications" },
  resume: { go: "applications", words: "Applications" },
};

/**
 * What stopped Auto in the last week, from the record itself: the questions
 * left for the person, by what would clear them, and the sends that stopped
 * after the fill. The same reading that was a hand-run SQL query until 16
 * Sept, so the biggest pile is always in view.
 */
function Stops({ onGo }: { onGo: (go: string) => void }) {
  const [days, setDays] = useState(7);
  const [r, setR] = useState<StopsAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retried, setRetried] = useState<string | null>(null);
  useEffect(() => {
    setR(null); setError(null);
    api<StopsAnswer>(`/api/auto/stops?days=${days}`).then(setR).catch((e) => setError(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message));
  }, [days, retried]);
  const rows = r ? (showAll ? r.questions : r.questions.slice(0, 12)) : [];
  const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const retryAll = async () => {
    setRetrying(true); setError(null);
    try {
      const a = await api<{ requeued: number }>("/api/applications/requeue-all", { method: "POST", body: "{}" });
      setRetried(`${a.requeued} back in Auto's queue. They go out by its rules, within your daily limit, and each gets a fresh record.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message);
    } finally {
      setRetrying(false);
    }
  };
  return (
    <div className="stops">
      <p className="small">
        <strong>What stopped Auto</strong>{" "}
        <span className="muted">— from the record, last</span>{" "}
        <select className="input inline" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="How many days back">
          <option value={1}>day</option><option value={7}>7 days</option><option value={30}>30 days</option>
        </select>
      </p>
      {error && <p className="err small">{error}</p>}
      {!r && !error && <p className="muted small">Reading the record…</p>}
      {r && r.fillsLeft === 0 && r.sends.length === 0 && (r.fails?.length ?? 0) === 0 && <p className="muted small">Nothing stopped in that time: every fill JobCork started went through, or none ran.</p>}
      {r && (r.requeueable ?? 0) > 0 && (
        <p className="small">
          <strong>{r.requeueable}</strong> application{r.requeueable === 1 ? " is" : "s are"} still waiting on you over a question. JobCork learns to answer more with each release, so what stopped them may fill now.{" "}
          <button className="btn small" onClick={() => { void retryAll(); }} disabled={retrying}>{retrying ? "Putting them back…" : "Try them all again"}</button>
        </p>
      )}
      {retried && <p className="small ok">{retried}</p>}
      {r && r.fillsLeft > 0 && (
        <>
          <p className="muted small">{r.fillsLeft} fill{r.fillsLeft === 1 ? "" : "s"} left something for you, on {r.applications} application{r.applications === 1 ? "" : "s"}. By what would clear it:</p>
          <ul className="small stops-buckets">
            {r.byBucket.map((b) => {
              const go = BUCKET_GO[b.bucket];
              return <li key={b.bucket}><strong>{b.count}</strong> — {b.words}{go && <> · <button className="step-link small" onClick={() => onGo(go.go)}>{go.words} →</button></>}</li>;
            })}
          </ul>
          <ul className="small stops-list">
            {rows.map((q) => (
              <li key={`${q.bucket}-${q.key}`}>
                <span className="stops-count">{q.count}×</span> <strong>{q.label}</strong>
                <span className="muted"> — {q.why}{q.employers.length > 0 && <> ({q.employers.join(", ")})</>} · last {day(q.lastAt)}</span>
              </li>
            ))}
          </ul>
          {r.questions.length > 12 && (
            <button className="step-link small" onClick={() => setShowAll(!showAll)}>{showAll ? "Fewer" : `All ${r.questions.length} questions`}</button>
          )}
        </>
      )}
      {r && (r.fails?.length ?? 0) > 0 && (
        <>
          <p className="muted small">Fills that failed outright — JobCork's fault or the site's, in the record's own words (a network one is tried again on its own):</p>
          <ul className="small stops-list">
            {r.fails!.map((f) => <li key={f.note}><span className="stops-count">{f.count}×</span> {f.note}{f.employers.length > 0 && <span className="muted"> ({f.employers.join(", ")})</span>}<span className="muted"> · last {day(f.lastAt)}</span></li>)}
          </ul>
        </>
      )}
      {r && r.sends.length > 0 && (
        <>
          <p className="muted small">Sends that stopped after a complete fill:</p>
          <ul className="small stops-list">
            {r.sends.map((s) => <li key={s.stop}><span className="stops-count">{s.count}×</span> {SEND_STOP_WORDS[s.stop] ?? s.stop}{s.employers.length > 0 && <span className="muted"> ({s.employers.join(", ")})</span>}<span className="muted"> · last {day(s.lastAt)}</span></li>)}
          </ul>
        </>
      )}
    </div>
  );
}

const SEND_STOP_WORDS: Record<string, string> = {
  human_check: "a human check (CAPTCHA) stood before Submit — those are yours to clear",
  spam_flag: "the form refused the send as spam — that employer now comes to you to press Submit",
  unconfirmed: "Submit was pressed and the page showed no confirmation",
  form_refused: "the form would not submit: a field it did not accept",
  handoff: "handed to you to press Submit",
  other: "other",
};

export default function AutoApply({ onSaved, onBack, onGo }: { onSaved: () => void; onBack: () => void; onGo: (go: string) => void }) {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [ext, setExt] = useState<ExtensionStatus | null>(null);
  const [keys, setKeys] = useState<RunnerKeyView[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [autoNote, setAutoNote] = useState<string | null>(null);

  const refreshAuto = useCallback(() => {
    void findExtension().then(setExt);
    api<{ keys: RunnerKeyView[] }>("/api/runner/keys").then((r) => setKeys(r.keys)).catch(() => setKeys([]));
  }, []);

  /** Issue a key and hand it to the extension in this browser. Auto must already be saved on. */
  const connectHere = async () => {
    setConnecting(true);
    setAutoNote(null);
    try {
      const found = await findExtension();
      if (!found.installed) { setAutoNote("The JobCork extension is not installed in this browser. See the Guide to set it up."); return; }
      const label = browserLabel();
      const issued = await api<{ key: string; id: string }>("/api/runner/keys", { method: "POST", body: JSON.stringify({ label }) });
      const took = await handRunnerKey(issued.key, label);
      if (!took) {
        await api(`/api/runner/keys/${issued.id}`, { method: "DELETE" }).catch(() => {});
        setAutoNote("The extension did not take the key. Reload the extension at chrome://extensions and try again.");
        return;
      }
      setAutoNote("Connected. Copilot starts within a couple of minutes and works whenever this browser is open.");
      refreshAuto();
    } catch (e) {
      setAutoNote(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message);
    } finally {
      setConnecting(false);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    setErrors([]);
    api<{ preferences: Preferences }>("/api/preferences")
      .then((r) => setPrefs(r.preferences))
      .catch((e) => setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);
  useEffect(refreshAuto, [refreshAuto]);

  if (loading) return <p className="muted">Loading…</p>;

  if (!prefs) {
    return (
      <>
        <header className="bar">
          <h1 className="tight">Couldn't load Auto-apply</h1>
          <button className="btn ghost small" onClick={onBack}>Back</button>
        </header>
        <ErrorList errors={errors} />
        <div className="row"><button className="btn" onClick={load}>Try again</button></div>
      </>
    );
  }

  const set = <K extends keyof Preferences>(k: K, v: Preferences[K]) => {
    setPrefs({ ...prefs, [k]: v });
    setSavedAt(null);
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const wasAuto = keys.length > 0 || (ext?.runner?.connected ?? false);
      const r = await api<{ preferences: Preferences; keysRevoked?: number }>("/api/preferences", {
        method: "PUT",
        body: JSON.stringify(prefs),
      });
      setPrefs(r.preferences);
      setSavedAt(Date.now());
      onSaved();
      if (r.preferences.applyMode === "auto") {
        if (!(ext?.runner?.connected)) await connectHere();
        else refreshAuto();
      } else if (wasAuto || (r.keysRevoked ?? 0) > 0) {
        await handRunnerKey(null);
        setAutoNote("Auto is off. Every connected browser has been disconnected.");
        refreshAuto();
      }
    } catch (e) {
      setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">Auto-apply</h1>
          <p className="muted small">Whether JobCork applies for you, and the rules it works by. New to the three ways it sends? <button className="step-link small" onClick={() => onGo("guide")}>See the Guide</button>.</p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <RadioGroup
        label="Mode"
        options={APPLY_MODES}
        value={prefs.applyMode}
        onChange={(v) => set("applyMode", v as Preferences["applyMode"])}
      />
      {prefs.applyMode === "auto" && (
        <AutoRules prefs={prefs} set={set} ext={ext} keys={keys} onConnect={() => { void connectHere(); }} connecting={connecting} note={autoNote} stamp={savedAt} onGo={onGo} />
      )}
      <Stops onGo={onGo} />

      <label className="field-label" htmlFor="cap">Daily limit</label>
      <p className="muted small field-hint">
        Capped on purpose. Volume lowers your interview rate — focused applications
        convert about twice as well.
      </p>
      <input
        id="cap" className="input narrow" type="number" min={0} max={50}
        value={prefs.dailyCap}
        onChange={(e) => set("dailyCap", Number(e.target.value))}
      />

      <ErrorList errors={errors} />

      <div className="row">
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        {savedAt && <span className="muted small saved">Saved</span>}
      </div>
    </>
  );
}
